/**
 * Tier-2 celebration: full-screen level-up "Number Slam" (design variant
 * "Level Up · Number Slam"). The new level number slams in over spinning rays,
 * a shockwave and a spark burst; then "LEVEL UP" text, an XP progress bar and a
 * Continue button rise on the design timeline. A rank-boundary crossing shows the
 * Badge Forge ceremony instead (routed upstream) — never this slam.
 *
 * Blocking modal: Continue is the only exit (no auto-dismiss, no scrim tap).
 * The level-up chime plays on mount; a light haptic fires at the slam impact.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Modal, Platform, Pressable, Text, View } from 'react-native';
import * as Haptics from 'expo-haptics';
import { LinearGradient } from 'expo-linear-gradient';
import { soundService } from '../../../shared/services/SoundService';
import { SOUND_IDS } from '../../../shared/constants/sounds.constants';
import {
  CELEBRATION_COLORS,
  CELEBRATION_TEST_IDS,
  LEVEL_SLAM,
  LEVEL_UP_BAR,
} from '../../constants/celebrations.constants';
import { getLevelUpBarPlan } from './levelUpCelebration.helper';
import { getLevelUpCelebrationStyles } from './LevelUpCelebration.styles';
import { LevelSlamStage } from './LevelSlamStage';

const USE_NATIVE = Platform.OS !== 'web';
const DESIGN_EASE = Easing.bezier(0.16, 1, 0.3, 1);

const Rise: React.FC<{ delay: number; children: React.ReactNode }> = ({ delay, children }) => {
  const y = useRef(new Animated.Value(18)).current;
  const op = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(y, { toValue: 0, duration: 500, delay, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
      Animated.timing(op, { toValue: 1, duration: 500, delay, useNativeDriver: USE_NATIVE }),
    ]).start();
    // Mount-only fixed timeline
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Animated.View style={{ opacity: op, transform: [{ translateY: y }], alignSelf: 'stretch', alignItems: 'center' }}>
      {children}
    </Animated.View>
  );
};

export interface LevelUpCelebrationProps {
  level: number;
  xpCurrent: number;
  xpTarget: number;
  /** Pre-earn fill (0..1) the bar starts from; omitted → near-full default. */
  startProgress?: number;
  onDismiss: () => void;
  testID?: string;
}

export const LevelUpCelebration: React.FC<LevelUpCelebrationProps> = ({
  level,
  xpCurrent,
  xpTarget,
  startProgress,
  onDismiss,
  testID,
}) => {
  const styles = useMemo(() => getLevelUpCelebrationStyles(), []);
  const barPlan = useMemo(
    () => getLevelUpBarPlan({ xpCurrent, xpTarget, startProgress }),
    [xpCurrent, xpTarget, startProgress]
  );

  const scrimOp = useRef(new Animated.Value(0)).current;
  // Bar starts at the pre-earn fill, climbs to full, resets, then fills to the remainder.
  const barFill = useRef(new Animated.Value(barPlan.from)).current;

  useEffect(() => {
    void soundService.play(SOUND_IDS.LEVEL_UP);

    Animated.timing(scrimOp, {
      toValue: 1,
      duration: LEVEL_SLAM.SCRIM_IN_MS,
      useNativeDriver: USE_NATIVE,
    }).start();

    // Fill to complete → hold → snap back to 0 at the boundary → fill to the remainder.
    Animated.sequence([
      Animated.delay(LEVEL_SLAM.BAR_FILL_DELAY_MS),
      Animated.timing(barFill, { toValue: 1, duration: LEVEL_UP_BAR.FILL_MS, easing: DESIGN_EASE, useNativeDriver: false }),
      Animated.delay(LEVEL_UP_BAR.HOLD_FULL_MS),
      Animated.timing(barFill, { toValue: 0, duration: LEVEL_UP_BAR.RESET_MS, easing: DESIGN_EASE, useNativeDriver: false }),
      Animated.timing(barFill, { toValue: barPlan.end, duration: LEVEL_UP_BAR.REFILL_MS, easing: DESIGN_EASE, useNativeDriver: false }),
    ]).start();

    const hapticTimer = setTimeout(() => {
      if (Platform.OS !== 'web') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {
          /* unsupported device */
        });
      }
    }, LEVEL_SLAM.IMPACT_MS);
    return () => clearTimeout(hapticTimer);
    // Mount-only: one fixed timeline per celebration (host remounts via key)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      visible
      transparent
      animationType="none"
      onRequestClose={onDismiss}
      statusBarTranslucent
      presentationStyle="overFullScreen"
      testID={testID ?? CELEBRATION_TEST_IDS.LEVEL_UP}
    >
      <Animated.View style={[styles.scrim, { opacity: scrimOp }]}>
        <LevelSlamStage level={level} />

        <View style={styles.textBlock}>
          <Rise delay={LEVEL_SLAM.RISE_EYEBROW_MS}>
            <Text style={styles.eyebrow}>LEVEL UP</Text>
          </Rise>
          <Rise delay={LEVEL_SLAM.RISE_HEADING_MS}>
            <Text style={styles.heading} testID={CELEBRATION_TEST_IDS.LEVEL_UP_LEVEL}>
              You hit Level {level} ⚡
            </Text>
          </Rise>
          <Rise delay={LEVEL_SLAM.RISE_CHIP_MS}>
            <View style={styles.chip}>
              <Text style={styles.chipText}>Keep the streak alive 🔥</Text>
            </View>
          </Rise>
        </View>

        <Rise delay={LEVEL_SLAM.RISE_XP_MS}>
          <View style={styles.xpBlock}>
            <View style={styles.barTrack}>
              <Animated.View
                testID={CELEBRATION_TEST_IDS.LEVEL_UP_BAR_FILL}
                style={[
                  styles.barFill,
                  { width: barFill.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) },
                ]}
              >
                <LinearGradient
                  colors={[CELEBRATION_COLORS.GOLD, CELEBRATION_COLORS.MINT]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{ flex: 1 }}
                />
              </Animated.View>
            </View>
            <View style={styles.barLabels}>
              <Text style={styles.barLabelText} testID={CELEBRATION_TEST_IDS.LEVEL_UP_XP}>
                {xpCurrent.toLocaleString()} / {xpTarget.toLocaleString()} XP
              </Text>
              <Text style={styles.barLabelText} testID={CELEBRATION_TEST_IDS.LEVEL_UP_NEXT}>
                Level {level + 1}
              </Text>
            </View>
          </View>
        </Rise>

        <Rise delay={LEVEL_SLAM.RISE_BUTTON_MS}>
          <Pressable
            style={({ pressed }) => [styles.continueBtn, pressed && styles.btnPressed]}
            onPress={onDismiss}
            accessibilityRole="button"
            accessibilityLabel={`Continue. You reached level ${level}.`}
            testID={CELEBRATION_TEST_IDS.LEVEL_UP_CONTINUE}
          >
            <Text style={styles.continueText}>Continue</Text>
          </Pressable>
        </Rise>
      </Animated.View>
    </Modal>
  );
};
